/**
 * S3Connector — AWS S3 / S3-compatible object storage filesystem-face connector.
 * Mounts by syncing objects to a local directory.
 *
 * Every filesystem call here is async and deadline-bounded. `ctx.mountTarget`
 * resolves against the workspace directory, so an s3 connector declared with
 * `mount.target: workspace` writes straight onto the rclone FUSE mount — and
 * `downloadAll` is re-entered from the live `filesystem.sync` face, not only at
 * connect time. A synchronous write there parks the whole event loop in an
 * uninterruptible kernel wait: a hang, not an error, so `try/catch` never fires.
 */

import { mkdir, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";
import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  FilesystemFace,
  SyncOptions,
  SyncResult,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";
import { FsDeadlineError, fsDeadlineMs, withFsDeadline } from "@skaile/workspaces/core";

const s3Pkg = "@aws-sdk/client-s3";

/**
 * Deadline multiplier for the whole object-download loop.
 *
 * The single-op deadline catches a wedged mount; a bucket-sized download is
 * different, because its honest duration scales with the object count. The
 * multiplier is spent as **one shared budget** across the loop rather than
 * per object, so a wedged mount cannot turn N objects into N × the deadline.
 * Mirrors `COPY_DEADLINE_FACTOR` in the local driver.
 */
const DOWNLOAD_DEADLINE_FACTOR = 20;

/**
 * Minimal shape of the AWS S3 SDK module used internally.
 * Declared here so tests can provide a lightweight fake without importing the
 * real (optional) `@aws-sdk/client-s3` package.
 */
export interface S3SdkModule {
  S3Client: new (
    config: object,
  ) => {
    send(cmd: object): Promise<any>;
    destroy(): void;
  };
  ListObjectsV2Command: new (input: object) => object;
  GetObjectCommand: new (input: object) => object;
}

interface S3State {
  client: InstanceType<S3SdkModule["S3Client"]>;
  bucket: string;
  prefix: string;
  region: string;
}

/**
 * Filesystem-face connector for AWS S3 and S3-compatible object storage — downloads all
 * objects from a bucket prefix into a local directory on connect and re-downloads on sync.
 * Requires the `@aws-sdk/client-s3` optional peer dependency.
 * @docLink packages/factory-assets/concepts#s3-connector
 */
export class S3Connector extends AbstractConnector {
  readonly name = "s3";

  /** Optional SDK module override — used in tests to avoid importing the real AWS SDK. */
  private _sdkOverride?: S3SdkModule;

  constructor(sdkOverride?: S3SdkModule) {
    super();
    this._sdkOverride = sdkOverride;
  }

  readonly filesystem: FilesystemFace = {
    sync: (handle: ConnectorHandle, options?: SyncOptions): Promise<SyncResult> => {
      return this._sync(handle, options);
    },
  };

  private async _loadSdk(): Promise<S3SdkModule> {
    if (this._sdkOverride) return this._sdkOverride;
    return (await import(/* @vite-ignore */ s3Pkg)) as S3SdkModule;
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const targetDir = ctx.mountTarget;
    if (!targetDir) {
      throw new Error("S3Connector requires ctx.mountTarget to be set by the manager");
    }
    const secrets = ctx.secrets;

    const started = Date.now();
    const opts = declaration.options;
    const bucket = String(opts?.bucket ?? "");
    if (!bucket) {
      this.log.error("connect failed: missing bucket option", undefined);
      throw new Error("S3Connector requires options.bucket");
    }

    const prefix = String(opts?.prefix ?? "");
    const region = String(opts?.region ?? "us-east-1");
    const endpoint = opts?.endpoint ? String(opts.endpoint) : undefined;
    this.log.info("mount start", { bucket, prefix, region, endpoint, targetDir });

    const s3Mod = await this._loadSdk();
    const { S3Client } = s3Mod;

    const clientConfig: any = { region };
    if (endpoint) clientConfig.endpoint = endpoint;

    // Pass the profile via the client config rather than mutating process.env
    // (AWS_PROFILE), which leaks process-wide and is never restored.
    const profile = secrets?.resolve(declaration.auth ?? "");
    if (profile) {
      clientConfig.profile = profile;
    }

    const client = new S3Client(clientConfig);
    const state: S3State = { client, bucket, prefix, region };

    // Initial sync: download objects to targetDir
    await withFsDeadline(`mkdir ${targetDir}`, () => mkdir(targetDir, { recursive: true }));
    try {
      const result = await this.downloadAll(state, targetDir);
      this.log.info("mount ok", {
        targetDir,
        filesWritten: result.filesWritten,
        durationMs: Date.now() - started,
      });
      return this.makeHandle(declaration, state, targetDir);
    } catch (err) {
      this.log.error("connect failed", err, { bucket, targetDir });
      throw err;
    }
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("unmount");
    const { client } = handle.state as S3State;
    client.destroy();
  }

  private async _sync(handle: ConnectorHandle, _options?: SyncOptions): Promise<SyncResult> {
    this.log.debug("sync start");
    const state = handle.state as S3State;
    try {
      const result = await this.downloadAll(state, handle.mountPath!);
      this.log.debug("sync ok", { filesWritten: result.filesWritten });
      return result;
    } catch (err) {
      this.log.error("sync failed", err);
      throw err;
    }
  }

  /**
   * Fetch one object's bytes, or `null` when it yields none.
   *
   * S3-side failures are contained here — a single `GetObject` miss is
   * survivable and must not abort the whole sync. Local filesystem failures are
   * deliberately *not* in scope: see the write path in {@link downloadAll}.
   */
  private async fetchObject(
    s3Mod: S3SdkModule,
    state: S3State,
    key: string,
  ): Promise<Buffer | null> {
    try {
      const resp = await state.client.send(
        new s3Mod.GetObjectCommand({ Bucket: state.bucket, Key: key }),
      );
      // transformToByteArray preserves binary content; transformToString would
      // UTF-8 decode and corrupt non-text objects.
      const bytes = await resp.Body?.transformToByteArray();
      return bytes == null ? null : Buffer.from(bytes);
    } catch (err) {
      this.log.warn("object download failed", {
        key,
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }

  private async downloadAll(state: S3State, targetDir: string): Promise<SyncResult> {
    const s3Mod = await this._loadSdk();
    const { client, bucket, prefix } = state;

    // List all objects
    const entries: Array<{ key: string; relPath: string }> = [];
    let continuationToken: string | undefined;

    do {
      const resp = await client.send(
        new s3Mod.ListObjectsV2Command({
          Bucket: bucket,
          Prefix: prefix,
          MaxKeys: 1000,
          ContinuationToken: continuationToken,
        }),
      );

      for (const obj of resp.Contents ?? []) {
        const relPath =
          prefix && obj.Key?.startsWith(prefix) ? obj.Key.slice(prefix.length) : obj.Key;
        if (!relPath) continue;
        entries.push({ key: obj.Key, relPath });
      }

      continuationToken = resp.IsTruncated ? resp.NextContinuationToken : undefined;
    } while (continuationToken);

    // Download each file. One shared filesystem budget for the whole loop, and
    // a per-directory memo so a thousand objects under one prefix cost one
    // `mkdir` round-trip on the mount instead of a thousand.
    let filesWritten = 0;
    let bytesTransferred = 0;
    const budgetMs = fsDeadlineMs() * DOWNLOAD_DEADLINE_FACTOR;
    const deadlineAt = Date.now() + budgetMs;
    const created = new Set<string>();

    for (const { key, relPath } of entries) {
      // Out of budget: fail loudly. Returning a short `filesWritten` would
      // present a half-populated mount as a completed sync.
      if (deadlineAt - Date.now() <= 0) {
        throw new FsDeadlineError(`downloadAll ${targetDir}`, budgetMs);
      }

      const body = await this.fetchObject(s3Mod, state, key);
      if (body == null) continue;

      // No catch around the local writes: unlike an S3 miss, ENOSPC / EACCES /
      // EROFS / a missed deadline says the mount cannot take the rest of the
      // bucket either — swallowing it is how a half-populated mount reads as done.
      const localPath = join(targetDir, relPath);
      const parent = dirname(localPath);
      if (!created.has(parent)) {
        await withFsDeadline(
          `mkdir ${parent}`,
          () => mkdir(parent, { recursive: true }),
          deadlineAt - Date.now(),
        );
        created.add(parent);
      }
      await withFsDeadline(
        `writeFile ${localPath}`,
        () => writeFile(localPath, body),
        deadlineAt - Date.now(),
      );
      filesWritten++;
      bytesTransferred += body.byteLength;
    }

    return { filesWritten, filesDeleted: 0, bytesTransferred };
  }
}

/**
 * Creates a new S3Connector instance.
 * @param sdkOverride - Optional SDK module to use instead of dynamically importing
 *   `@aws-sdk/client-s3`. Intended for testing only.
 * @returns Configured S3Connector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#s3-connector-factory
 */
export function createConnector(sdkOverride?: S3SdkModule): S3Connector {
  return new S3Connector(sdkOverride);
}
